import type { Auth0ContextInterface, GetTokenSilentlyOptions } from '@auth0/auth0-react';
import { Auth0Context, Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import type { ReactNode } from 'react';
import * as React from 'react';
import { isRecoverableAuthSessionError } from '@/lib/auth/sessionRecovery';
import { ACTIVE_ORG_STORAGE_KEY } from '@/lib/utils/orgRoutes';

interface AuthProviderProps {
  children: ReactNode;
}

type AccessTokenResult = Awaited<ReturnType<Auth0ContextInterface['getAccessTokenSilently']>>;

function AuthSessionRecoveryProvider({ children }: AuthProviderProps) {
  const auth0 = useAuth0();
  const recoveryStartedRef = React.useRef(false);

  // Wrap logout so every useAuth0().logout() caller (nav menu, session
  // recovery) drops the persisted active-org pin. Otherwise the next user to
  // sign in on this browser would inherit the previous user's org through
  // AuthProvider's bootstrap pin (ENG-1217 review).
  const logout = React.useCallback(
    (options?: Parameters<Auth0ContextInterface['logout']>[0]) => {
      try {
        window.localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      } catch {
        // localStorage unavailable -- nothing to clear.
      }
      return auth0.logout(options);
    },
    [auth0.logout]
  ) as Auth0ContextInterface['logout'];

  const beginSessionRecovery = React.useCallback(
    (error: unknown) => {
      if (recoveryStartedRef.current) {
        return;
      }

      recoveryStartedRef.current = true;
      console.warn('[Auth0] Session can no longer mint access tokens; forcing re-login', error);
      void logout({ logoutParams: { returnTo: window.location.origin } });
    },
    [logout]
  );

  // Auth0 exposes this method as overloads. The wrapper preserves the
  // underlying runtime behavior, but TypeScript cannot infer overloaded
  // async callbacks through React.useCallback.
  const getAccessTokenSilently = React.useCallback(
    async (options?: GetTokenSilentlyOptions): Promise<AccessTokenResult> => {
      try {
        return await auth0.getAccessTokenSilently(options);
      } catch (error) {
        if (isRecoverableAuthSessionError(error)) {
          beginSessionRecovery(error);
        }
        throw error;
      }
    },
    [auth0.getAccessTokenSilently, beginSessionRecovery]
  ) as Auth0ContextInterface['getAccessTokenSilently'];

  const value = React.useMemo<Auth0ContextInterface>(
    () => ({
      ...auth0,
      getAccessTokenSilently,
      logout,
    }),
    [auth0, getAccessTokenSilently, logout]
  );

  return <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>;
}

/**
 * Wraps Auth0Provider with application-specific configuration.
 * Handles organization invitation params from URL for invite flows.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // Check for organization invitation params in URL
  // Auth0 redirects here with ?organization=org_xxx&invitation=inv_xxx after invitation acceptance
  const urlParams = new URLSearchParams(window.location.search);
  const invitation = urlParams.get('invitation');

  // Resolve the org to pin into authorizationParams. Pinning it means the Auth0
  // SPA SDK replays the org on *silent* token renewals, so a background refresh
  // can't drop the organization and let the post-login action reassign the user
  // to an arbitrary membership -- the org-switcher drift in ENG-1217.
  //
  // Priority: an explicit ?organization= (invitation-accept flow) wins;
  // otherwise the last *validated* active org persisted by useOrgClaims. We do
  // NOT pin the org from the URL *path* here: Auth0Provider builds its client
  // once from these initial props and never re-reads them, so (a) an org-less
  // landing URL like /home would bootstrap org-less and still drift before it's
  // normalized, and (b) a stale/inaccessible bookmark like /org_revoked/... would
  // fail the bootstrap grant into a hard auth-error state instead of OrgUrlSync's
  // graceful inaccessible-org fallback. The persisted value is already-validated
  // (it was a real token org) and survives an org-less landing; cross-org URL
  // navigation is still handled post-init by OrgUrlSync via its recoverable
  // refresh-token switch. First login (no persisted org) bootstraps org-less as
  // before, then useOrgClaims persists the org for subsequent renewals.
  let storedOrg: string | null = null;
  try {
    storedOrg = window.localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  } catch {
    // localStorage unavailable (private mode / blocked) -- fall through.
  }
  const organization = urlParams.get('organization') ?? storedOrg ?? undefined;

  if (organization || invitation) {
    if (import.meta.env.DEV) {
      console.log('[Auth0] Detected invitation params:', { organization, invitation });
    }
  }

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      // Persist refresh_token in localStorage so Auth0's SPA SDK can
      // renew the browser session, and for the back-channel
      // ``refresh_token`` grant used by org switching (mirrors the CLI's
      // switch_org and bypasses Auth0's silent-SSO path that drops the
      // ``organization`` parameter). Workspace starts no longer touch
      // this RT — they run a dedicated native-client device flow.
      //
      // ``useRefreshTokensFallback`` lets the SDK fall back to silent
      // SSO via the Auth0 iframe when an RT isn't available, so users on
      // an existing session don't get bounced through a full login.
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        // ``offline_access`` tells Auth0 to issue a refresh token
        // alongside the access token. Without it the org-switch flow
        // and the browser session renewal both break.
        scope: 'openid profile email org_name org_id offline_access',
        // Pass organization/invitation params if present (from invitation redirect)
        ...(organization && { organization }),
        ...(invitation && { invitation }),
      }}
    >
      <AuthSessionRecoveryProvider>{children}</AuthSessionRecoveryProvider>
    </Auth0Provider>
  );
}
